import { useEffect, useMemo, useRef } from "react";
import { DayCellMountArg, EventInput } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import deLocale from "@fullcalendar/core/locales/de";
import FullCalendar from "@fullcalendar/react";
import multimonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import { Box, Button, DialogActions, DialogContent, FormHelperText, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import { Row } from "../../misc/Row";
import { SelectArrayInput } from "react-admin";
import { TimePicker } from "@mui/x-date-pickers";
import { InstructorInput } from "../../inputs/InstructorInput";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { Column } from "../../misc/Column";
import { renderAddress } from "../../misc/AddressDisplay";
import { SelectInput } from "../../inputs/SelectInput";
import { TheoryLectureCategory } from "../../model/TheoryLecture";
import { TheoryCourseLesson } from "./TheoryCourseWizard";
import { HoverableNumberCircle } from "../../misc/HoverableNumberCircle";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { smallSelectInputStyle } from "../../misc/backofficeTheme";
import { useThemeColor } from "../../hooks/useThemeColor";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";

const timePickerStyle = {
  "& .MuiInputBase-root": { maxWidth: "115px", minWidth: "115px" },
  "& .MuiInputBase-input": { py: "8.5px" },
};

export function PlanTheoryCourseForm(props: {
  goBack?: () => void;
  lessons: TheoryCourseLesson[];
  setLessons: React.Dispatch<React.SetStateAction<TheoryCourseLesson[]>>;
  drivingSchool: DrivingSchool;
  error?: string | null;
}) {
  const { goBack, lessons, setLessons, drivingSchool, error } = props;
  const errorColor = useThemeColor("error").main;
  const { watch, control, setValue } = useFormContext();
  const selectedContent = watch("content") as Array<TheoryLectureCategory>;
  const prevContentRef = useRef<Array<TheoryLectureCategory>>(["Grundstoff", "Zusatzstoff_B"]);
  const { branches } = useCurrentDrivingSchool();

  function addLesson(cellDetail: DateClickArg) {
    // avoid getting fired on clicking remove lesson icon
    if (
      cellDetail.jsEvent.target instanceof SVGSVGElement ||
      cellDetail.jsEvent.target instanceof SVGPathElement ||
      cellDetail.jsEvent.target instanceof HTMLButtonElement
    ) {
      return;
    }

    if (getNumLessons(selectedContent) === lessons.length) return;

    const cellDate = DateTime.fromJSDate(cellDetail.date);

    const targetedDateLessons = lessons.filter((lesson) => lesson.date.equals(cellDate));
    // Add max 4 lessons per day
    if (targetedDateLessons.length === 4) return;
    const parentTableNode = cellDetail.dayEl.closest("table");
    const newLesson: TheoryCourseLesson = {
      id: uuidv4(),
      date: cellDate,
      parentTableNode,
      label: "",
      instructorId: "",
      start: cellDate,
      location: "",
      theoryLecture: { category: "Grundstoff", lectureNumber: 0 },
    };
    const newLessons = [...lessons, newLesson];
    const sortedLessons = newLessons.sort((a, b) => a.date.toMillis() - b.date.toMillis());
    const sortedLabeledLessons = sortedLessons.map((lesson, index) => ({
      ...lesson,
      label: getLessonLabel(index, selectedContent),
    }));
    setLessons(sortedLabeledLessons);
  }

  function removeLesson(id: string) {
    const newLessons = lessons.filter((lesson) => lesson.id !== id);
    const sortedLessons = newLessons.sort((a, b) => a.date.toMillis() - b.date.toMillis());
    const sortedLabeledLessons = sortedLessons.map((lesson, index) => ({
      ...lesson,
      label: getLessonLabel(index, selectedContent),
    }));
    setLessons(sortedLabeledLessons);
  }

  function getDayLessons(date: Date): TheoryCourseLesson[] {
    const dayDate = DateTime.fromJSDate(date);
    return lessons.filter((lesson) => lesson.date.equals(dayDate));
  }

  function renderDayCellContent(options: DayCellMountArg) {
    const dayLessons = getDayLessons(options.date);
    const isSixRowMonth = dayLessons[0]?.parentTableNode?.querySelectorAll("tr").length === 6;
    return (
      <Row
        sx={{
          p: 0,
          maxWidth: "45px",
          minWidth: "45px",
          height: isSixRowMonth ? "38px" : "47px",
          position: "absolute",
          top: "0%",
          right: 0,
          zIndex: 1,
          gap: "3px",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <Row
          sx={{ width: "100%", height: "100%", justifyContent: "center", alignItems: "center", position: "relative" }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {options.dayNumberText}
          </Typography>
          {dayLessons.map(({ label, id }, dayIndex) => (
            <Box
              key={id}
              sx={{
                top: dayIndex < 2 ? "2px" : "auto",
                bottom: dayIndex >= 2 ? "2px" : "auto",
                left: dayIndex % 2 ? "auto" : "1px",
                right: dayIndex % 2 ? "1px" : "auto",
              }}
              position="absolute"
            >
              <HoverableNumberCircle onClick={() => removeLesson(id)} label={label} />
            </Box>
          ))}
        </Row>
      </Row>
    );
  }

  useEffect(() => {
    const numLessons = getNumLessons(selectedContent);
    const updatedLabeledLessons = lessons.reduce((acc, lesson, index) => {
      if (index >= numLessons) {
        return acc;
      }
      return [...acc, { ...lesson, label: getLessonLabel(index, selectedContent) }];
    }, [] as TheoryCourseLesson[]);
    setLessons(updatedLabeledLessons);
  }, [selectedContent]);

  let contentChoices: Array<{ label: string; value: TheoryLectureCategory }> = [
    { label: "Grundstoff", value: "Grundstoff" },
    { label: "Zusatzstoff Klasse A", value: "Zusatzstoff_A" },
    { label: "Zusatzstoff Klasse B", value: "Zusatzstoff_B" },
  ];
  if (drivingSchool.customizations.includes("tractors")) {
    contentChoices = contentChoices.concat([
      { label: "Zusatzstoff Klasse L", value: "Zusatzstoff_L" },
      { label: "Zusatzstoff Klasse T", value: "Zusatzstoff_T" },
    ]);
  }

  const calendarEventInputs = useMemo(() => lessons.map(lessonToEventInput), [lessons]);

  return (
    <>
      <DialogContent>
        <Column>
          <Row sx={{ gap: 2.5, alignItems: "start" }}>
            <SelectArrayInput
              label="Kursinhalte"
              className="fix-SelectArrayInput-label"
              source="content"
              optionText="label"
              optionValue="value"
              choices={contentChoices}
              onChange={(event) => {
                const selection = event.target.value as Array<TheoryLectureCategory>;
                const isGrundstoffSelected = selection.includes("Grundstoff");
                const selectedZusatzstoff = selection.filter((c) => c !== "Grundstoff");
                let selection_ = selection;
                // Always put Grundstoff at the beginning of the array, if selected ...
                if (isGrundstoffSelected && selectedContent[0] !== "Grundstoff") {
                  selection_ = ["Grundstoff", ...selectedZusatzstoff];
                }
                // Ensure that only one Zusatzstoff category can be selected ...
                if (selectedZusatzstoff.length > 1) {
                  const newlyAdded = selectedZusatzstoff.find((it) => !prevContentRef.current.includes(it));
                  if (newlyAdded) {
                    selection_ = selection_.filter((c) => c === "Grundstoff" || c === newlyAdded);
                  }
                }
                if (selection_ !== selection) {
                  setValue("content", selection_);
                }
                prevContentRef.current = selection_;
              }}
              validate={(value) => (value?.length > 0 ? null : "Bitte auswählen.")}
            />
            {branches.length > 1 && (
              <SelectInput
                source="location"
                label="Filiale"
                options={branches.map((it) => [
                  it.uid,
                  `${it.name} (${renderAddress(it.postalAddress, { oneLine: true })})`,
                ])}
                sx={{ ...smallSelectInputStyle, maxWidth: "200px" }}
                validate={(value) => (value ? true : "Bitte auswählen.")}
              />
            )}
            <InstructorInput
              source="instructorId"
              branchId={watch("location") as string | undefined}
              searchable
              sx={smallSelectInputStyle}
            />
            <Controller
              name="start"
              control={control}
              rules={{
                validate: (value) => {
                  if (!value || (value.get("hour") === 0 && value.get("minute") === 0)) {
                    return "Bitte eingeben.";
                  }
                  return true;
                },
              }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <TimePicker
                  label="Startzeit"
                  value={value}
                  sx={timePickerStyle}
                  onChange={onChange}
                  disableOpenPicker={false}
                  ampm={false}
                  slotProps={{
                    textField: {
                      error: !!error,
                      helperText: error?.message && <span style={{ whiteSpace: "nowrap" }}>{error?.message}</span>,
                    },
                  }}
                />
              )}
            />
            <SelectInput
              source="breakDuration"
              label="Pause zwischen Terminen"
              sx={{
                "& .MuiOutlinedInput-root": {
                  width: "165px",
                  maxWidth: "165px",
                  minWidth: "165px",
                  minHeight: "37px",
                  boxSizing: "border-box",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "4.5px 12px",
                },
              }}
              options={[
                [0, "0 Minuten"],
                [15, "15 Minuten"],
                [30, "30 Minuten"],
                [45, "45 Minuten"],
                [60, "60 Minuten"],
              ]}
              validate={() => true}
            />
          </Row>
          <Row sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Bitte wähle die Tage aus, an denen der Theorieunterricht stattfinden soll.
              <br />
              Wenn an einem Tag mehrere Theorielektionen stattfinden soll, den Tag einfach mehrfach anklicken.
            </Typography>
          </Row>
          <Box
            sx={{
              border: `1px solid ${error ? errorColor : "var(--fc-border-color)"}`,
              borderRadius: "8px",
              padding: "16px",
            }}
          >
            <FullCalendar
              multiMonthMinWidth={280}
              headerToolbar={headerToolbar}
              dayCellContent={renderDayCellContent}
              eventDisplay="none"
              timeZone="Europe/Berlin"
              locales={[deLocale]}
              height="auto"
              plugins={[dayGridPlugin, multimonthPlugin, interactionPlugin]}
              initialView="sixMonths"
              views={{
                sixMonths: {
                  type: "multiMonth",
                  duration: { months: 6 },
                  multiMonthMaxColumns: 3,
                  fixedWeekCount: false,
                },
              }}
              initialDate={DateTime.now().toISO()}
              editable={true}
              dayMaxEvents={2}
              nowIndicator={true}
              events={calendarEventInputs}
              dateClick={addLesson}
            />
            {error && <FormHelperText error>{error}</FormHelperText>}
          </Box>
        </Column>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        {goBack ? (
          <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={goBack}>
            Zurück
          </Button>
        ) : (
          <div />
        )}
        <Button type="submit" endIcon={<NavigateNextIcon />} variant="contained">
          Weiter
        </Button>
      </DialogActions>
    </>
  );
}

const lessonToEventInput = (lesson: TheoryCourseLesson, index: number): EventInput => ({
  editable: false,
  extendedProps: {
    index,
    lesson,
  },
});

const getLessonLabel = (index: number, theoryCourseContent: Array<TheoryLectureCategory>): string => {
  if (theoryCourseContent.includes("Grundstoff")) {
    if (index < 12) {
      return `${index + 1}`;
    }
    index -= 12;
  }
  const otherCategory = theoryCourseContent.find((c) => c !== "Grundstoff");
  if (otherCategory) {
    const labelPrefix = otherCategory.slice(-1);
    return `${labelPrefix}${index + 1}`;
  }
  return "?";
};

export function getNumLessons(theoryCourseContent: Array<TheoryLectureCategory>): number {
  return theoryCourseContent.reduce((numLessons, category) => {
    if (category === "Grundstoff") {
      return numLessons + 12;
    } else if (category === "Zusatzstoff_A") {
      return numLessons + 4;
    } else if (category === "Zusatzstoff_B") {
      return numLessons + 2;
    } else if (category === "Zusatzstoff_L") {
      return numLessons + 2;
    } else if (category === "Zusatzstoff_T") {
      return numLessons + 6;
    } else {
      throw new Error(`Unexpected category: ${category}`);
    }
  }, 0);
}

const headerToolbar = { left: "title", center: "", right: "prev today next" };
